/**
 * StatusRing — the one canonical at-a-glance status glyph for tasks / issues.
 *
 * Linear-style progress ring: the circle fills by workflow progress
 * (empty → half → three-quarters → full), so a status reads as *how far along*
 * the work is, not merely which color-category it's in. One component, one
 * status→fill mapping, used everywhere a task/issue status appears (list rows,
 * board cards, breadcrumbs, detail headers, Play cards). Never forked.
 *
 *   <StatusRing status="in_progress" />
 *   <StatusRing status="done" size="md" />
 *
 * Color intent resolves through the SAME token source as StatusDot/StatusBadge
 * (`TASK_STATUS_INTENT`), so the color grammar stays consistent — only the
 * *shape* (a filling ring) is added.
 *
 * Accessibility: the glyph is NOT color-only. The fill fraction carries the
 * state visually for colorblind users, and every ring gets an `aria-label`
 * (its human status label) for screen readers.
 */

import * as React from "react";
import { cn } from "../utils";
import { TASK_STATUS_INTENT, type Intent } from "../tokens";
import { type TaskStatus } from "../types/projects";

// The six task/issue workflow states, in lifecycle order. Alias of the shared
// TaskStatus so there is one status vocabulary, not a parallel ring-only one.
export type TaskRingStatus = TaskStatus;

/**
 * The two DISPLAY-ONLY ring states — derived at render time, never stored.
 *
 * A machine-driven task can die (its last run exited non-zero) and a task can
 * turn out to be a copy of another one. Both are real things a reader must see,
 * and NEITHER is a new row value: `failed` is read off the last run, `duplicate`
 * off the link. So they widen the ring's vocabulary and stop there — no
 * migration, no CHECK to widen, no synced column that older peers can't
 * represent. The DB enum (`TaskStatus`) is untouched, and the gate asserts it
 * stays that way.
 */
export type RingDisplayState = "failed" | "duplicate";

/** Everything the ring can draw: the six stored statuses + the two derived ones. */
export type RingState = TaskRingStatus | RingDisplayState;

/**
 * status → fill fraction + shape. Empty→½→¾→full is the Linear metaphor; the
 * exact fractions live here as the single source of truth (mirrored by the
 * gate test). `dashed` = not-yet-committed (backlog); `strike` = cancelled;
 * `check` = the terminal done fill; `bang` = failed; `dupe` = duplicate.
 *
 * ── Fill means PROGRESS, so a bad ending is not a full ring ─────────────────
 * `failed` and `duplicate` both sit at fill 0 and carry their meaning in a
 * SILHOUETTE instead. It is tempting to give `failed` the filled disc that
 * `done` gets — both are endings — but fill answers "how far did this get", and
 * a run that died at 20% did not get far. `cancelled` already establishes the
 * precedent: terminal, but empty.
 */
export const RING_SHAPE: Record<
  RingState,
  {
    fill: number;
    dashed?: boolean;
    strike?: boolean;
    check?: boolean;
    bang?: boolean;
    dupe?: boolean;
  }
> = {
  backlog: { fill: 0, dashed: true },
  todo: { fill: 0 },
  in_progress: { fill: 0.5 },
  review: { fill: 0.75 },
  done: { fill: 1, check: true },
  cancelled: { fill: 0, strike: true },
  failed: { fill: 0, bang: true },
  duplicate: { fill: 0, dupe: true },
};

const RING_LABEL: Record<RingState, string> = {
  backlog: "Backlog",
  todo: "Todo",
  in_progress: "In progress",
  review: "In review",
  done: "Done",
  cancelled: "Cancelled",
  failed: "Failed",
  duplicate: "Duplicate",
};

/** The ring's own word for a state. Exported so a caller that needs the label
 *  without the glyph (a legend, a picker, a tooltip built elsewhere) reads it
 *  from the same map the glyph does. */
export function ringLabel(state: RingState): string {
  return RING_LABEL[state] ?? state;
}

/**
 * Default tone for the derived states. The six stored ones resolve through
 * `TASK_STATUS_INTENT` exactly as before; these two have no entry there (they
 * are not statuses), so their tone lives with their shape.
 *
 * `failed` is the one place the ring is allowed to go red. That is not a hole in
 * the color model — the ring never GRADES work in flight, but it may name how
 * work ENDED, and "badly" is one of the ways. Health of a LIVE thing is still
 * exclusively the attention mark's job.
 */
const DISPLAY_INTENT: Record<RingDisplayState, Intent> = {
  failed: "danger",
  duplicate: "neutral",
};

// Intent → pure text color (drives `stroke="currentColor"`). Kept local rather
// than added to INTENT_CLASSES so this new atom can't destabilize the
// app-wide token map; promote to a token surface later if another block needs it.
const INTENT_TEXT: Record<Intent, string> = {
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  neutral: "text-muted-foreground",
};

const SIZE: Record<"xs" | "sm" | "md", string> = {
  xs: "size-3",
  sm: "size-3.5",
  md: "size-4",
};

// Geometry: 16-unit viewBox, r=6, strokeWidth=2. C = 2πr.
const R = 6;
const C = 2 * Math.PI * R;

export function StatusRing({
  status,
  size = "sm",
  className,
  title,
  intent: intentOverride,
}: {
  status: RingState;
  size?: keyof typeof SIZE;
  className?: string;
  /** Overrides the default aria-label / tooltip (defaults to the status name). */
  title?: string;
  /**
   * Overrides the tone, keeping the SHAPE. For callers that borrow the ring's
   * fill vocabulary for a lifecycle that isn't a task's — a goal's phase, a
   * project's — where `TASK_STATUS_INTENT`'s tone for the equivalent task
   * status would be wrong. Omit and the tone resolves from the shared registry
   * exactly as before, so every existing call site is byte-identical.
   */
  intent?: Intent;
}) {
  const shape = RING_SHAPE[status] ?? RING_SHAPE.todo;
  const intent: Intent =
    intentOverride ??
    DISPLAY_INTENT[status as RingDisplayState] ??
    TASK_STATUS_INTENT[status as TaskRingStatus] ??
    "neutral";
  const label = title ?? RING_LABEL[status] ?? status;
  const { fill, dashed, strike, check, bang, dupe } = shape;

  return (
    <svg
      viewBox="0 0 16 16"
      role="img"
      aria-label={label}
      className={cn("shrink-0", SIZE[size], INTENT_TEXT[intent], className)}
    >
      <title>{label}</title>
      {/* Base ring. Solid for backlog/todo (the whole outline reads); faint for
          partially-filled states so the bright arc shows progress against it.
          `duplicate` skips it — it draws its own PAIR of rings below, and a
          third concentric one would just be noise behind them. */}
      {!dupe && (
        <circle
          cx="8"
          cy="8"
          r={R}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeOpacity={fill > 0 ? 0.25 : dashed ? 0.6 : 0.9}
          strokeDasharray={dashed ? "1.6 2.4" : undefined}
        />
      )}
      {/* Progress arc — the filled fraction, drawn bright, starting at 12 o'clock. */}
      {fill > 0 && fill < 1 && (
        <circle
          cx="8"
          cy="8"
          r={R}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${fill * C} ${C}`}
          transform="rotate(-90 8 8)"
        />
      )}
      {/* Done — filled disc + check in the page background color. */}
      {check && (
        <>
          <circle cx="8" cy="8" r={R} fill="currentColor" />
          <path
            d="M5 8.2 L7.1 10.3 L11.2 5.7"
            fill="none"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ stroke: "var(--background)" }}
          />
        </>
      )}
      {/* Cancelled — muted outline struck through. */}
      {strike && (
        <line
          x1="5.2"
          y1="5.2"
          x2="10.8"
          y2="10.8"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      )}
      {/* Failed — the ring at full weight with a bang inside it.
          Proportioned off lucide's circle-alert (bar and dot at the same
          fractions of the box) but drawn at THIS family's geometry (r=6,
          strokeWidth 2), so a failed ring sits at the same optical weight in a
          16px slot as every other ring instead of arriving visibly thinner.
          Sharing the bang with the attention mark is deliberate: a bang means
          "something is wrong here" in both channels, and its POSITION says
          which — in the state slot the WORK broke, after the title something
          ABOUT it needs you. The row never shows both at once (WorkRow
          suppresses the mark on a failed row), so they cannot be confused. */}
      {bang && (
        <>
          <line
            x1="8"
            y1="5.2"
            x2="8"
            y2="8.3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <circle cx="8" cy="10.6" r="0.85" fill="currentColor" />
        </>
      )}
      {/* Duplicate — two overlapping rings, the universal copy mark, kept inside
          the ring family rather than borrowing a second geometry.
          Back ring up-left and faint, front ring down-right and bright: the same
          arrangement lucide's `copy` uses, so it reads as a copy rather than as
          two unrelated circles.
          The PAIR is sized to span the full 16 box (not each ring shrunk inside
          it) — a first pass at r=4 centred tightly rendered a mark visibly
          smaller and lighter than every other ring in the column, which in a list
          reads as a different weight class rather than a different state.
          The knockout disc punches a gap out of the back ring where the front one
          crosses it; without it the two strokes tangle into a blob at 14px. Same
          `var(--background)` trick the done check already uses. */}
      {dupe && (
        <>
          <circle
            cx="5.9"
            cy="5.9"
            r="4.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeOpacity={0.5}
          />
          <circle cx="10.1" cy="10.1" r="6.15" style={{ fill: "var(--background)" }} />
          <circle
            cx="10.1"
            cy="10.1"
            r="4.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeOpacity={0.9}
          />
        </>
      )}
    </svg>
  );
}
